<template>
  <div class="ai-chat-widget" v-if="isLoggedIn" :style="widgetStyle" :class="{ 'pos-left': posicaoEsquerda }">
    <transition name="chat-panel">
      <div v-if="isOpen" class="chat-panel">
        <div class="chat-header">
          <div class="chat-header-info">
            <div class="chat-avatar">
              <img v-if="avatarUrl" :src="avatarUrl" class="chat-avatar-img" alt="avatar" />
              <feather-icon v-else icon="CpuIcon" size="18" />
            </div>
            <div>
              <h6 class="mb-0">Assistente SalesFlow</h6>
              <small>Powered by IA</small>
            </div>
          </div>
          <div class="chat-header-actions">
            <button class="btn-icon" @click="clearChat" title="Nova conversa">
              <feather-icon icon="RefreshCwIcon" size="14" />
            </button>
            <button class="btn-icon" @click="isOpen = false" title="Fechar">
              <feather-icon icon="XIcon" size="16" />
            </button>
          </div>
        </div>

        <div class="chat-messages" ref="messagesContainer">
          <div v-if="messages.length === 0" class="chat-welcome">
            <div class="welcome-icon">
              <feather-icon icon="MessageCircleIcon" size="32" />
            </div>
            <h6>Olá! Como posso ajudar?</h6>
            <p>{{ welcomeMessage }}</p>
            <div v-if="optinPendente" class="chat-optin">
              <p class="mb-50">{{ widgetConfig.mensagem_optin }}</p>
              <button class="quick-action-btn optin-btn" @click="aceitarOptin">Aceitar e continuar</button>
            </div>
            <div class="quick-actions" v-if="!optinPendente">
              <button
                v-for="qa in visibleQuickActions"
                :key="qa.label || qa"
                class="quick-action-btn"
                @click="sendQuickAction(qa.mensagem || qa)"
              >{{ qa.label || qa }}</button>
            </div>
          </div>

          <div
            v-for="(msg, idx) in messages"
            :key="idx"
            class="chat-message"
            :class="msg.role"
          >
            <div class="message-avatar" v-if="msg.role === 'assistant'">
              <feather-icon icon="CpuIcon" size="14" />
            </div>
            <div class="message-bubble" @click="handleMessageClick">
              <div v-if="msg.attachments && msg.attachments.length" class="message-attachments">
                <div
                  v-for="(att, aidx) in msg.attachments"
                  :key="aidx"
                  class="attachment-preview"
                >
                  <img v-if="att.type === 'image'" :src="att.data" class="att-image" />
                  <div v-else class="att-file">
                    <feather-icon icon="FileTextIcon" size="16" />
                    <span>{{ att.name }}</span>
                  </div>
                </div>
              </div>
              <div v-if="msg.content" class="message-text" v-html="formatMessage(msg.content)"></div>
              <div v-if="msg.actionLinks && msg.actionLinks.length" class="action-links">
                <button
                  v-for="(link, lidx) in msg.actionLinks"
                  :key="lidx"
                  class="action-link-btn"
                  @click.stop="navigateTo(link.route)"
                >
                  <feather-icon icon="ExternalLinkIcon" size="13" />
                  <span>{{ link.label }}</span>
                </button>
              </div>
              <div v-if="msg.role === 'assistant' && msg.loading" class="typing-indicator">
                <span></span><span></span><span></span>
              </div>
            </div>
          </div>
        </div>

        <div v-if="pendingFiles.length" class="pending-files">
          <div v-for="(file, fidx) in pendingFiles" :key="fidx" class="pending-file">
            <img v-if="file.type === 'image'" :src="file.data" class="pending-thumb" />
            <div v-else class="pending-file-icon">
              <feather-icon icon="FileTextIcon" size="14" />
            </div>
            <span class="pending-name">{{ file.name }}</span>
            <button class="pending-remove" @click="removePendingFile(fidx)">
              <feather-icon icon="XIcon" size="12" />
            </button>
          </div>
        </div>

        <div class="chat-input-area">
          <button class="btn-attach" @click="$refs.fileInput.click()" title="Anexar arquivo">
            <feather-icon icon="PaperclipIcon" size="16" />
          </button>
          <input
            type="file"
            ref="fileInput"
            class="d-none"
            accept="image/*,.pdf"
            multiple
            @change="onFileSelect"
          />
          <textarea
            ref="chatInput"
            v-model="inputText"
            placeholder="Digite sua mensagem... (Ctrl+V para colar imagem)"
            rows="1"
            class="chat-textarea"
            @keydown.enter.exact.prevent="sendMessage"
            @paste="onPaste"
            @input="autoResize"
          ></textarea>
          <button
            class="btn-send"
            :disabled="(!inputText.trim() && !pendingFiles.length) || isStreaming"
            @click="sendMessage"
          >
            <feather-icon v-if="!isStreaming" icon="SendIcon" size="16" />
            <b-spinner v-else small />
          </button>
        </div>
      </div>
    </transition>

    <button
      class="chat-fab"
      :class="{ 'has-unread': hasUnread }"
      @click="toggleChat"
    >
      <feather-icon v-if="!isOpen" icon="MessageCircleIcon" size="22" />
      <feather-icon v-else icon="XIcon" size="22" />
    </button>
  </div>
</template>

<script>
import axios from '@/libs/axios';
import ToastificationContent from '@/@core/components/toastification/ToastificationContent.vue';
import { resolvePastedImageSource } from '@/libs/catalogImageMatch';

export default {
  name: 'AiChatWidget',
  data() {
    return {
      isOpen: false,
      messages: [],
      inputText: '',
      pendingFiles: [],
      isStreaming: false,
      hasUnread: false,
      conversationId: null,
      quickActions: [
        'Meus últimos pedidos',
        'Criar um pedido',
        'Cadastrar novo cliente',
        'Consultar comissões',
      ],
      widgetConfig: null,
      optinAceito: false,
      // Controle interno para abortar streaming ativo no logout
      activeStreamReader: null,
      logoutResetGeneration: 0,
    };
  },
  created() {
    this.conversationId = this.generateConversationId();
    this.optinAceito = localStorage.getItem('aiChatOptinAceito') === '1';
    this.carregarWidgetConfig();

    // Escuta o evento central de logout emitido por auth/LOGOUT_USER
    this.$root.$on('auth:logout', this._onLogout);
    window.addEventListener('salesflow:logout', this._onLogout);
  },
  beforeDestroy() {
    this.$root.$off('auth:logout', this._onLogout);
    window.removeEventListener('salesflow:logout', this._onLogout);
  },
  computed: {
    isLoggedIn() {
      return this.$store?.getters['auth/IS_LOGGED_IN'] || !!localStorage.getItem('accessToken');
    },
    comissoesAtivo() {
      return this.$store?.getters['preferencias/comissoesAtivo'] !== false;
    },
    visibleQuickActions() {
      // Ações rápidas configuradas pelo admin (já filtradas por perfil no
      // backend); sem configuração, usa as ações padrão.
      const cfgActions = this.widgetConfig?.quick_actions;
      if (Array.isArray(cfgActions) && cfgActions.length) return cfgActions;
      return this.quickActions.filter((qa) => {
        if (!this.comissoesAtivo && /comiss/i.test(qa)) return false;
        return true;
      });
    },
    welcomeMessage() {
      if (this.widgetConfig?.mensagem_boas_vindas) return this.widgetConfig.mensagem_boas_vindas;
      return this.comissoesAtivo
        ? 'Pergunte sobre pedidos, produtos, comissões ou envie imagens e documentos para análise.'
        : 'Pergunte sobre pedidos, produtos ou envie imagens e documentos para análise.';
    },
    widgetStyle() {
      const cor = this.widgetConfig?.cor_primaria;
      return cor ? { '--primary': cor } : {};
    },
    posicaoEsquerda() {
      return this.widgetConfig?.posicao === 'bottom-left';
    },
    avatarUrl() {
      return this.widgetConfig?.avatar_url || null;
    },
    optinPendente() {
      return !!this.widgetConfig?.mensagem_optin && !this.optinAceito;
    },
  },
  methods: {
    async carregarWidgetConfig() {
      try {
        const { data } = await axios.get('/ai-chat/widget-config');
        this.widgetConfig = data?.data || null;
      } catch (e) {
        this.widgetConfig = null; // sem config: visual padrão
      }
    },
    aceitarOptin() {
      this.optinAceito = true;
      localStorage.setItem('aiChatOptinAceito', '1');
    },
    toggleChat() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.hasUnread = false;
        this.$nextTick(() => {
          this.$refs.chatInput?.focus();
          this.scrollToBottom();
        });
      }
    },
    clearChat() {
      this.messages = [];
      this.pendingFiles = [];
      this.inputText = '';
      // Nova conversa = novo conversationId → o backend abandona a sessão de
      // pedido (carrinho) da conversa anterior.
      this.conversationId = this.generateConversationId();
    },
    /**
     * Reset completo disparado pelo evento auth:logout / salesflow:logout.
     * Aborta qualquer streaming ativo, limpa todo o estado do widget e gera
     * um novo conversationId — sem apagar aiChatOptinAceito.
     */
    _onLogout() {
      // Incrementa a "geração" para invalidar callbacks do streaming corrente
      this.logoutResetGeneration += 1;

      // Aborta o reader de streaming se houver um ativo
      if (this.activeStreamReader) {
        try { Promise.resolve(this.activeStreamReader.cancel()).catch(() => {}); } catch (_) { /* noop */ }
        this.activeStreamReader = null;
      }

      // Limpa todo o estado do chat
      this.messages = [];
      this.inputText = '';
      this.pendingFiles = [];
      this.hasUnread = false;
      this.isOpen = false;
      this.isStreaming = false;
      this.conversationId = this.generateConversationId();

      // NÃO apaga: aiChatOptinAceito (mantido por requisito)
    },
    generateConversationId() {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
      }
      // Fallback seguro o bastante para um identificador opaco de conversa.
      return 'c-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
    },
    sendQuickAction(text) {
      this.inputText = text;
      this.sendMessage();
    },
    formatMessage(text) {
      if (!text) return '';
      let html = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/`(.*?)`/g, '<code>$1</code>')
        .replace(/\n/g, '<br>');
      html = html.replace(
        /(\/(?:operacoes|cadastros|crm)[^\s<)]+)/g,
        '<a href="$1" class="chat-link" data-route="$1">$1</a>'
      );
      return html;
    },
    removerLinkDetalhePedido(text) {
      if (!text) return '';
      const rotaDetalhePedido = '(?:https?:\\/\\/[^\\s)<]+)?\\/?(?:[^\\s/)<]+\\/)?b2bhub\\/operacoes\\/pedido-vendas\\/detalhes\\/[^\\s)<]+|\\/operacoes\\/pedido-vendas\\/detalhes\\/[^\\s)<]+';
      const fraseComLink = new RegExp(
        `Você pode visualizar(?: os)? detalhes do pedido(?: através (?:deste|do) link)?\\s*:?\\s*\\(?${rotaDetalhePedido}\\)?\\.?`,
        'gi',
      );
      const linkIsolado = new RegExp(`\\(?${rotaDetalhePedido}\\)?`, 'gi');

      return String(text)
        .replace(fraseComLink, '')
        .replace(linkIsolado, '')
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n{3,}/g, '\n\n');
    },
    handleMessageClick(e) {
      const link = e.target.closest('.chat-link');
      if (link) {
        e.preventDefault();
        const route = link.getAttribute('data-route');
        if (route) {
          this.$router.push(route).catch(() => {});
          this.isOpen = false;
        }
      }
    },
    navigateTo(route) {
      if (route) {
        this.$router.push(route).catch(() => {});
        this.isOpen = false;
      }
    },
    autoResize() {
      const el = this.$refs.chatInput;
      if (!el) return;
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 100) + 'px';
    },
    async onPaste(e) {
      const items = e.clipboardData?.items;
      if (!items) return;

      // Guarda o HTML do clipboard ANTES do await (o event pode ser reciclado).
      const clipboardData = e.clipboardData;
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          const blob = item.getAsFile();
          await this.processFile(blob);
          // Identificação determinística: src do <img> copiado (HTML do
          // clipboard) ou comparação local com as imagens da página.
          const last = this.pendingFiles[this.pendingFiles.length - 1];
          if (last && last.type === 'image' && !last.sourceImageUrl) {
            try {
              const src = await resolvePastedImageSource(clipboardData, last.data);
              if (src) this.$set(last, 'sourceImageUrl', src);
            } catch (err) { /* pista opcional — segue sem ela */ }
          }
        }
      }
    },
    onFileSelect(e) {
      const files = e.target.files;
      for (const file of files) {
        this.processFile(file);
      }
      e.target.value = '';
    },
    async processFile(file) {
      if (file.size > 4 * 1024 * 1024) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Aviso',
            text: 'Arquivo muito grande. Máximo: 4MB',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        });
        return;
      }

      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (evt) => {
          const isImage = file.type.startsWith('image/');
          this.pendingFiles.push({
            type: isImage ? 'image' : 'pdf',
            name: file.name || (isImage ? 'imagem.png' : 'documento.pdf'),
            data: evt.target.result,
          });
          resolve();
        };
        reader.readAsDataURL(file);
      });
    },
    removePendingFile(idx) {
      this.pendingFiles.splice(idx, 1);
    },
    async sendMessage() {
      const text = this.inputText.trim();
      if (!text && !this.pendingFiles.length) return;
      if (this.isStreaming) return;
      if (this.optinPendente) return; // exige aceite do opt-in configurado

      const userMessage = {
        role: 'user',
        content: text,
        attachments: [...this.pendingFiles],
      };

      this.messages.push(userMessage);
      this.inputText = '';
      this.pendingFiles = [];
      this.$nextTick(() => {
        this.autoResize();
        this.scrollToBottom();
      });

      const assistantMsg = { role: 'assistant', content: '', loading: true };
      this.messages.push(assistantMsg);
      this.isStreaming = true;

      // Captura a geração atual para detectar se o logout ocorreu durante o streaming
      const myGeneration = this.logoutResetGeneration;

      try {
        const apiMessages = [];
        for (const m of this.messages) {
          if (m.loading) continue;
          if (m.role === 'assistant' && m.toolExchange && m.toolExchange.length) {
            // Reenvia o histórico de tool calls (assistant.tool_calls + role=tool)
            // ANTES da resposta final — preserva IDs reais entre turnos.
            for (const tm of m.toolExchange) apiMessages.push(tm);
          }
          apiMessages.push({
            role: m.role,
            content: m.content,
            attachments: m.attachments,
          });
        }

        const token = localStorage.getItem('accessToken');
        const baseURL = axios.defaults.baseURL || (window.location.origin + '/api');

        // Esta chamada de streaming usa fetch() cru e NÃO passa pelo interceptor
        // do libs/axios.js. Por isso precisamos anexar manualmente os mesmos
        // headers de tenant/perfil — caso contrário o backend não resolve o
        // tenant correto e retorna 404 "Usuário não existe!".
        const headers = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        };

        const impersonateSlug = localStorage.getItem('dkpImpersonateSlug');
        if (impersonateSlug && impersonateSlug !== 'dkp') {
          headers['x-tenant-slug'] = 'dkp';
          headers['x-admin-view-tenant'] = impersonateSlug;
        } else {
          let userData = {};
          try { userData = JSON.parse(localStorage.getItem('userData') || '{}'); } catch (e) { userData = {}; }
          const isUserMaster = Number(userData.usuario_master) === 1;
          headers['x-tenant-slug'] = isUserMaster
            ? 'dkp'
            : (localStorage.getItem('tenantSlug') || process.env.VUE_APP_TENANT_SLUG || 'dkp');
        }

        try {
          const perfilLogado = JSON.parse(localStorage.getItem('perfilLogado') || '{}');
          if (perfilLogado && perfilLogado.id_perfil_acesso != null) {
            headers['x-perfil-id'] = String(perfilLogado.id_perfil_acesso);
          }
        } catch (e) { /* noop */ }

        const response = await fetch(`${baseURL}/ai-chat/stream`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ messages: apiMessages, conversationId: this.conversationId }),
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        if (!response.body) {
          const json = await response.json().catch(() => null);
          assistantMsg.loading = false;
          assistantMsg.content = json?.data || 'Resposta recebida sem streaming.';
          this.$forceUpdate();
          return;
        }

        const reader = response.body.getReader();
        // Registra o reader ativo para que _onLogout possa abortá-lo
        this.activeStreamReader = reader;
        const decoder = new TextDecoder();
        let buffer = '';

        // eslint-disable-next-line no-constant-condition
        while (true) {
          // Se o logout foi disparado durante o streaming, para imediatamente
          if (this.logoutResetGeneration !== myGeneration) break;

          const { done, value } = await reader.read();
          if (done) break;

          // Verifica novamente após o await (pode ter chegado logout enquanto aguardava)
          if (this.logoutResetGeneration !== myGeneration) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            const payload = line.slice(6).trim();
            if (payload === '[DONE]') continue;

            try {
              const parsed = JSON.parse(payload);
              if (parsed.content) {
                assistantMsg.loading = false;
                assistantMsg.content += parsed.content;
                if (assistantMsg.ocultarLinkDetalhePedido) {
                  assistantMsg.content = this.removerLinkDetalhePedido(assistantMsg.content);
                }
                this.$forceUpdate();
                this.scrollToBottom();
              }
              if (parsed.action) {
                assistantMsg.loading = false;
                if (parsed.action.type === 'executing') {
                  const actionLabels = {
                    criar_cliente: 'Criando cliente',
                    criar_rascunho_pedido: 'Criando rascunho de pedido',
                    criar_pic: 'Criando PIC',
                    iniciar_pedido: 'Iniciando pedido',
                    adicionar_item_pedido: 'Adicionando item ao pedido',
                    finalizar_pedido: 'Gravando pedido',
                  };
                  const label = actionLabels[parsed.action.action] || 'Executando ação';
                  assistantMsg.content += `\n\n⏳ **${label}...**\n`;
                  this.$forceUpdate();
                  this.scrollToBottom();
                }
                if (parsed.action.type === 'tool_exchange' && Array.isArray(parsed.action.messages)) {
                  // Guardado junto à resposta (não exibido) e reenviado nos
                  // próximos turnos para o modelo não reconstruir IDs.
                  assistantMsg.toolExchange = (assistantMsg.toolExchange || []).concat(parsed.action.messages);
                }
                if (parsed.action.type === 'created' && parsed.action.result) {
                  const r = parsed.action.result;
                  if (r.link) {
                    if (!assistantMsg.actionLinks) assistantMsg.actionLinks = [];
                    assistantMsg.actionLinks.push({
                      label: r.mensagem || 'Acessar',
                      route: r.link,
                    });
                    if (/\/operacoes\/pedido-vendas\/detalhes\//.test(r.link)) {
                      assistantMsg.ocultarLinkDetalhePedido = true;
                      assistantMsg.content = this.removerLinkDetalhePedido(assistantMsg.content);
                    }
                  }
                  this.$forceUpdate();
                  this.scrollToBottom();
                }
              }
              if (parsed.error) {
                assistantMsg.loading = false;
                assistantMsg.content = 'Desculpe, ocorreu um erro ao processar sua mensagem.';
                this.$forceUpdate();
              }
            } catch (parseErr) {
              // skip
            }
          }
        }

        // Limpa referência ao reader após conclusão normal
        if (this.activeStreamReader === reader) {
          this.activeStreamReader = null;
        }

        // Se o logout foi disparado, não repopula as mensagens (widget já foi limpo)
        if (this.logoutResetGeneration !== myGeneration) return;

        assistantMsg.loading = false;
        if (!assistantMsg.content) {
          assistantMsg.content = 'Desculpe, não consegui gerar uma resposta. Tente novamente.';
        }
        this.$forceUpdate();
      } catch (err) {
        // Se o reader foi cancelado pelo logout, ignora o erro silenciosamente
        if (this.logoutResetGeneration !== myGeneration) return;
        console.error('AI Chat error:', err);
        assistantMsg.loading = false;
        assistantMsg.content = 'Erro de conexão. Verifique sua internet e tente novamente.';
        this.$forceUpdate();
      } finally {
        // Só restaura isStreaming se o widget não foi resetado pelo logout
        if (this.logoutResetGeneration === myGeneration) {
          this.isStreaming = false;
          this.scrollToBottom();
        }
      }
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },
  },
};
</script>

<style scoped>
.ai-chat-widget {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 9999;
  font-family: inherit;
}
.ai-chat-widget.pos-left {
  right: auto;
  left: 20px;
}
.ai-chat-widget.pos-left .chat-panel {
  right: auto;
  left: 0;
}
.chat-avatar-img {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
}
.chat-optin {
  background: var(--body-bg, #f1f5f9);
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 8px;
  font-size: 0.85rem;
}
.optin-btn {
  font-weight: 600;
}

.chat-fab {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: var(--primary, #2563eb);
  color: #fff;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 16px rgba(37, 99, 235, 0.4);
  transition: all 0.2s ease;
}
.chat-fab:hover {
  transform: scale(1.05);
  box-shadow: 0 6px 20px rgba(37, 99, 235, 0.5);
}
.chat-fab.has-unread::after {
  content: '';
  position: absolute;
  top: 2px;
  right: 2px;
  width: 12px;
  height: 12px;
  background: #dc2626;
  border-radius: 50%;
  border: 2px solid #fff;
}

.chat-panel {
  position: absolute;
  bottom: 64px;
  right: 0;
  width: 380px;
  max-width: calc(100vw - 32px);
  height: 540px;
  max-height: calc(100vh - 120px);
  background: var(--card-bg, #fff);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid var(--border-color, #e4e7ec);
}

.chat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: var(--primary, #2563eb);
  color: #fff;
  flex-shrink: 0;
}
.chat-header-info {
  display: flex;
  align-items: center;
  gap: 10px;
}
.chat-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: rgba(255,255,255,0.2);
  display: flex;
  align-items: center;
  justify-content: center;
}
.chat-header h6 {
  color: #fff;
  font-size: 0.875rem;
  font-weight: 600;
}
.chat-header small {
  color: rgba(255,255,255,0.75);
  font-size: 0.7rem;
}
.chat-header-actions {
  display: flex;
  gap: 4px;
}
.btn-icon {
  background: transparent;
  border: none;
  color: #fff;
  cursor: pointer;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}
.btn-icon:hover {
  background: rgba(255,255,255,0.2);
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.chat-messages::-webkit-scrollbar {
  width: 4px;
}
.chat-messages::-webkit-scrollbar-thumb {
  background: var(--border-color, #d4d4d4);
  border-radius: 4px;
}

.chat-welcome {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 24px 16px;
  gap: 8px;
}
.welcome-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: var(--subtle-bg, #f3f2f7);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary, #2563eb);
  margin-bottom: 4px;
}
.chat-welcome h6 {
  color: var(--text-heading, #5e5873);
  font-size: 0.9375rem;
  margin: 0;
}
.chat-welcome p {
  color: var(--text-muted, #b9b9c3);
  font-size: 0.8125rem;
  margin: 0;
  line-height: 1.4;
}
.quick-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
  justify-content: center;
}
.quick-action-btn {
  background: var(--subtle-bg, #f3f2f7);
  border: 1px solid var(--border-color, #e4e7ec);
  border-radius: 16px;
  padding: 4px 12px;
  font-size: 0.75rem;
  color: var(--primary, #2563eb);
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
}
.quick-action-btn:hover {
  background: var(--primary, #2563eb);
  color: #fff;
  border-color: var(--primary, #2563eb);
}

.chat-message {
  display: flex;
  gap: 8px;
  max-width: 88%;
}
.chat-message.user {
  align-self: flex-end;
  flex-direction: row-reverse;
}
.chat-message.assistant {
  align-self: flex-start;
}
.message-avatar {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: var(--subtle-bg, #f3f2f7);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary, #2563eb);
  flex-shrink: 0;
  margin-top: 2px;
}
.message-bubble {
  padding: 8px 12px;
  border-radius: 12px;
  font-size: 0.8125rem;
  line-height: 1.5;
  word-break: break-word;
}
.user .message-bubble {
  background: var(--primary, #2563eb);
  color: #fff;
  border-bottom-right-radius: 4px;
}
.assistant .message-bubble {
  background: var(--subtle-bg, #f3f2f7);
  color: var(--text-body, #6e6b7b);
  border-bottom-left-radius: 4px;
}

.message-text >>> strong {
  font-weight: 600;
}
.message-text >>> code {
  background: rgba(0,0,0,0.08);
  padding: 1px 4px;
  border-radius: 3px;
  font-size: 0.75rem;
}
.user .message-text >>> code {
  background: rgba(255,255,255,0.2);
}

.message-attachments {
  margin-bottom: 6px;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.att-image {
  max-width: 180px;
  max-height: 120px;
  border-radius: 8px;
  object-fit: cover;
}
.user .att-image {
  border: 1px solid rgba(255,255,255,0.3);
}
.att-file {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 6px;
  background: rgba(0,0,0,0.06);
  font-size: 0.75rem;
}
.user .att-file {
  background: rgba(255,255,255,0.2);
}

.typing-indicator {
  display: flex;
  gap: 4px;
  padding: 4px 0;
}
.typing-indicator span {
  width: 6px;
  height: 6px;
  background: var(--text-muted, #b9b9c3);
  border-radius: 50%;
  animation: typing 1.4s infinite both;
}
.typing-indicator span:nth-child(2) { animation-delay: 0.2s; }
.typing-indicator span:nth-child(3) { animation-delay: 0.4s; }
@keyframes typing {
  0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
  30% { transform: translateY(-4px); opacity: 1; }
}

.pending-files {
  display: flex;
  gap: 6px;
  padding: 6px 12px;
  overflow-x: auto;
  border-top: 1px solid var(--border-color, #e4e7ec);
  flex-shrink: 0;
}
.pending-file {
  display: flex;
  align-items: center;
  gap: 4px;
  background: var(--subtle-bg, #f3f2f7);
  border-radius: 6px;
  padding: 4px 6px;
  font-size: 0.7rem;
  position: relative;
  max-width: 120px;
}
.pending-thumb {
  width: 28px;
  height: 28px;
  border-radius: 4px;
  object-fit: cover;
}
.pending-file-icon {
  color: var(--primary, #2563eb);
}
.pending-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text-body, #6e6b7b);
}
.pending-remove {
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--text-muted, #b9b9c3);
  padding: 0;
  display: flex;
  flex-shrink: 0;
}
.pending-remove:hover {
  color: #dc2626;
}

.chat-input-area {
  display: flex;
  align-items: flex-end;
  gap: 6px;
  padding: 10px 12px;
  border-top: 1px solid var(--border-color, #e4e7ec);
  background: var(--card-bg, #fff);
  flex-shrink: 0;
}
.btn-attach {
  background: transparent;
  border: none;
  color: var(--text-muted, #b9b9c3);
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  transition: color 0.15s;
}
.btn-attach:hover {
  color: var(--primary, #2563eb);
}
.chat-textarea {
  flex: 1;
  resize: none;
  border: 1px solid var(--border-color, #e4e7ec);
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 0.8125rem;
  line-height: 1.4;
  max-height: 100px;
  background: var(--input-bg, #fff);
  color: var(--text-body, #6e6b7b);
  outline: none;
  font-family: inherit;
  transition: border-color 0.15s;
}
.chat-textarea::placeholder {
  color: var(--text-muted, #b9b9c3);
}
.chat-textarea:focus {
  border-color: var(--primary, #2563eb);
}
.btn-send {
  background: var(--primary, #2563eb);
  border: none;
  color: #fff;
  cursor: pointer;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: opacity 0.15s;
}
.btn-send:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.btn-send:not(:disabled):hover {
  opacity: 0.9;
}

.chat-panel-enter-active,
.chat-panel-leave-active {
  transition: all 0.25s ease;
}
.chat-panel-enter,
.chat-panel-leave-to {
  opacity: 0;
  transform: translateY(16px) scale(0.95);
}

@media (max-width: 480px) {
  .chat-panel {
    width: calc(100vw - 16px);
    height: calc(100vh - 90px);
    bottom: 60px;
    right: -12px;
    border-radius: 12px 12px 0 0;
  }
  .ai-chat-widget {
    bottom: 16px;
    right: 16px;
  }
}

.dark-layout .chat-panel {
  background: var(--card-bg, #283046);
  border-color: var(--border-color, #3b4253);
}
.dark-layout .chat-input-area {
  background: var(--card-bg, #283046);
}
.dark-layout .chat-textarea {
  background: var(--input-bg, #283046);
  color: var(--text-body, #b4b7bd);
  border-color: var(--border-color, #3b4253);
}
.dark-layout .message-bubble {
  color: var(--text-body, #b4b7bd);
}
.dark-layout .assistant .message-bubble {
  background: var(--subtle-bg, #343d55);
}
.dark-layout .chat-welcome h6 {
  color: var(--text-heading, #d0d2d6);
}
.dark-layout .chat-optin {
  background: #343d55;
  border: 1px solid #4b5567;
  color: #f4f5f7;
}
.dark-layout .chat-optin p {
  color: #f4f5f7;
}
.dark-layout .chat-optin .optin-btn {
  background: var(--primary, #2563eb);
  border-color: var(--primary, #2563eb);
  color: #fff;
}
.dark-layout .chat-optin .optin-btn:hover {
  background: #fff;
  border-color: #fff;
  color: var(--primary, #2563eb);
}
.dark-layout .quick-action-btn {
  background: var(--subtle-bg, #343d55);
  border-color: var(--border-color, #3b4253);
}

.action-links {
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.action-link-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  background: var(--primary, #2563eb);
  color: #fff;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
}
.action-link-btn:hover {
  opacity: 0.9;
  transform: translateY(-1px);
}

.message-text >>> .chat-link {
  color: var(--primary, #2563eb);
  text-decoration: underline;
  cursor: pointer;
  font-weight: 500;
}
.message-text >>> .chat-link:hover {
  opacity: 0.8;
}
</style>
